import { useEffect, useState } from 'react'
import {
  CATALOG_MOTORS,
  DRIVE_SPEC,
  MOTORS,
  MISSING_MOTOR_DATA,
  MOTOR_BY_ID,
  OPEN_QUESTIONS,
  SET_AXES,
  SET_AXIS_COUNT,
  SET_MOTOR_COUNT,
  SET_MOTOR_MASS,
  TOE_SPRING,
  targetFor,
  toeMoment,
  type AxisStatus,
  type DriveProposal,
} from '@/domain/hs1'
import { Alert, Badge, Button, Card, Table, Td, Th } from '@/components/ui'
import { StagePanel } from '@/components/stage'
import { useSimulation } from '@/sim/SimulationProvider'
import { CycleChart } from '@/sim/CycleChart'
import { Metric, PageHead, axisRow, num, pct } from '@/sim/parts'

/**
 * Wie eine gesetzte Achse angezeigt wird.
 *
 * Drei Stufen, drei Töne. „Bedingt" bekommt bewusst KEINEN Warnton im Sinne von
 * Fehler: es ist ein Engpass in der Reserve oder ein angenommener Motorwert,
 * kein Mangel der Auslegung. Erst „offen" — ein Konstruktionswert, der im CAD
 * fehlt und gesetzt ist — steht im Warnton.
 */
const STATUS_TONE: Record<AxisStatus, 'success' | 'neutral' | 'warning'> = {
  pass: 'success',
  conditional: 'neutral',
  open: 'warning',
}

const STATUS_LABEL: Record<AxisStatus, string> = {
  pass: 'bestanden',
  conditional: 'bedingt',
  open: 'offen',
}

/**
 * Antriebe — was jede Achse wirklich braucht.
 *
 * Die Liste wird bei jeder Änderung neu gerechnet und nirgends gespeichert. Wer
 * eine Knieachse verschiebt, bekommt eine andere Liste; wer die Kadenz erhöht,
 * auch. Eine gespeicherte Motorenliste wäre eine zweite Wahrheit und liefe beim
 * ersten verschobenen Punkt aus dem Ruder.
 *
 * ANFORDERUNG UND VORSCHLAG BLEIBEN GETRENNT. Die linke Tabelle ist Physik und
 * hängt an keinem Katalog. Die rechte ist eine Empfehlung, und ein
 * Motorexperte soll sie überstimmen können, ohne dass an der Anforderung etwas
 * wackelt.
 */

export function DrivesPage() {
  const { model, simulation, focus, setFocus } = useSimulation()
  const { drives, metrics, toe } = simulation
  const [side, setSide] = useState<'R' | 'L'>('R')
  const [showStructural, setShowStructural] = useState(true)

  /* Die Marke gehört zu DIESER Liste. Wer die Seite verlässt, nimmt sie mit —
     ein Ring an einem Gelenk auf der Parameterseite wäre eine Aussage über
     eine Tabelle, die man dort nicht sieht. */
  useEffect(() => () => setFocus(null), [setFocus])

  const rows = drives.filter(
    (proposal) => proposal.demand.side === side && (showStructural || !proposal.demand.structural),
  )
  const structural = drives.filter((p) => p.demand.side === side && p.demand.structural)

  /* Wie viele Achsen entschieden sind und wie viele davon knapp werden. 70 %
     ist kein Grenzwert, sondern die Schwelle, ab der ein Blick lohnt. */
  const geprueft = rows.filter((p) => p.setup !== null)
  const knapp = geprueft.filter(
    (p) => Math.max(p.setup!.use.speed, p.setup!.use.peak, p.setup!.use.continuous) > 0.7,
  )

  return (
    <div className="stack">
      <PageHead
        title="Antriebe"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {(['R', 'L'] as const).map((key) => (
              <Button
                key={key}
                size="sm"
                variant={side === key ? 'primary' : 'ghost'}
                onClick={() => setSide(key)}
              >
                {key === 'R' ? 'Rechts' : 'Links'}
              </Button>
            ))}
          </div>
        }
      />

      {/* Das Bühnenfenster steht auf jeder Seite, auf der man die Bewegung
          verstellt. Beim Scrollen heftet es sich oben an: der Kopftext wird
          einmal gelesen, die Figur wird immer gebraucht. */}
      <StagePanel />

      <div className="metrics">
        <Metric label="Achsen" value={rows.length} note={`${structural.length} davon ohne Antrieb`} />
        <Metric
          label="Grösste Ausnutzung"
          value={pct(metrics.worstMomentUse, 0)}
          note="Spitzenmoment gegen die Winkelfreiheit"
          warn={metrics.worstMomentUse > 0.9}
        />
        <Metric
          label="Ohne passenden Antrieb"
          value={metrics.infeasible}
          note={`aus ${MOTORS.length} Katalogeinträgen`}
          warn={metrics.infeasible > 0}
        />
        <Metric label="Halteverlust zulässig" value={`${DRIVE_SPEC.holdingLoss} W`} note="je Achse, Wicklung" />
      </div>

      <Card
        flush
        title="Anforderung am Abgang"
        actions={
          <Button variant="ghost" size="sm" onClick={() => setShowStructural((v) => !v)}>
            {showStructural ? 'Nur angetriebene Achsen' : 'Alle Achsen'}
          </Button>
        }
      >
        <Table>
          <thead>
            <tr>
              <Th>Achse</Th>
              <Th numeric>ω Spitze</Th>
              <Th numeric>α Spitze</Th>
              <Th numeric>M Spitze</Th>
              <Th numeric>M eff</Th>
              <Th numeric>M halt</Th>
              <Th numeric>P Spitze</Th>
              <Th numeric>W treib</Th>
              <Th numeric>W brems</Th>
              <Th numeric>Ausnutzung</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((proposal) => {
              const d = proposal.demand
              return (
                /* Ein Klick auf die Zeile setzt einen Ring an genau dieses
                   Gelenk im Bühnenfenster. Die Ebene bleibt dabei aussen vor:
                   Sagittal und Frontal sind DASSELBE Gelenk mit zwei
                   Freiheiten, und zwei Ringe an derselben Stelle wären zwei
                   Marken für einen Ort. */
                <tr
                  key={`${d.joint}-${d.plane}`}
                  {...axisRow(focus, setFocus, {
                    joint: d.joint,
                    side: d.side,
                    label: d.label,
                  })}
                >
                  <Td>
                    {d.label}
                    {d.structural && (
                      <>
                        {' '}
                        <Badge tone="neutral">Struktur</Badge>
                      </>
                    )}
                    {/* Eine stillgelegte Achse steht auf ihrem Rastwinkel. Die
                        Zahlen daneben sind dann Haltewerte und keine
                        Bewegungswerte — ohne diesen Hinweis liest man eine
                        Spitzenleistung von null als Fehler. */}
                    {d.stopped && (
                      <>
                        {' '}
                        <Badge tone="warning">
                          still bei {d.stopped.at}°{d.stopped.drive ? '' : ' · ausgebaut'}
                        </Badge>
                      </>
                    )}
                  </Td>
                  <Td numeric>{num(d.speed.peak, 1)} 1/s</Td>
                  <Td numeric>{num(d.acceleration.peak, 0)} 1/s²</Td>
                  <Td numeric>{num(d.moment.peak, 0)} Nm</Td>
                  <Td numeric>{num(d.moment.rms, 0)} Nm</Td>
                  <Td numeric>{num(d.holdingMoment, 0)} Nm</Td>
                  <Td numeric>{num(d.power.peak, 0)} W</Td>
                  <Td numeric>{num(d.workDriving, 0)} J</Td>
                  <Td numeric>{num(d.workBraking, 0)} J</Td>
                  <Td numeric className={d.momentUse !== null && d.momentUse > 0.9 ? 'cell-warn' : undefined}>
                    {d.momentUse === null ? <span className="cell-muted">—</span> : pct(d.momentUse, 0)}
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Card>

      {structural.length > 0 && (
        <Alert tone="info" title="Achsen ohne Winkelfreiheit tragen strukturell">
          {structural.map((p) => `${p.demand.label} (${num(p.demand.moment.peak, 0)} Nm)`).join(', ')}.
          Für diese Ebenen gibt es keinen Antrieb — das Moment geht in Lager und Struktur.
        </Alert>
      )}

      {/* Nur, wo es ein Zehengelenk gibt. Der Gen5-Fuss ist starr; die Karte
          zeigte sonst eine Feder für eine Achse, die es nicht gibt. */}
      {model.dofs.mtp.y && <ToeCard toe={toe} limit={model.dofs.mtp.y.moment} />}

      {/*
        DIE PRÜFUNG DER GETROFFENEN AUSWAHL — die wichtigste Tabelle der Seite.

        „Welchen Motor bräuchte man?" ist die interessante Frage, solange nichts
        entschieden ist. Danach lautet sie: hält die Auswahl der gerechneten
        Last stand? Diese Tabelle beantwortet sie, Achse für Achse, mit den
        Zahlen aus demselben Durchlauf, der auch die Kurven füllt.
      */}
      <Card
        flush
        title="Die getroffene Auswahl gegen die gerechnete Last"
        eyebrow={`${geprueft.length} Achsen entschieden · ${knapp.length} über 70 % ausgenutzt`}
      >
        <Table>
          <thead>
            <tr>
              <Th>Achse</Th>
              <Th>Verbaut</Th>
              <Th numeric>i</Th>
              <Th numeric>n Rotor</Th>
              <Th numeric>M Spitze</Th>
              <Th numeric>M eff</Th>
              <Th numeric>Ausnutzung</Th>
              <Th>Befund</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((proposal) => {
              const fit = proposal.setup
              if (!fit) return null
              const aus = Math.max(fit.use.speed, fit.use.peak, fit.use.continuous)
              return (
                <tr key={`s-${proposal.demand.joint}-${proposal.demand.plane}`}>
                  <Td>{proposal.demand.label}</Td>
                  <Td>{fit.motor.label}</Td>
                  <Td numeric>{num(fit.ratio, fit.ratio % 1 ? 3 : 0)}</Td>
                  <Td numeric>
                    {fit.motor.speedMax > 0 ? (
                      `${num(fit.motorSpeed, 0)} / ${fit.motor.speedMax}`
                    ) : (
                      <span className="cell-muted">{num(fit.motorSpeed, 0)} / —</span>
                    )}
                  </Td>
                  <Td numeric>
                    {num(fit.motorMomentPeak, 2)} / {num(fit.motor.momentPeak, 2)} Nm
                  </Td>
                  <Td numeric>
                    {num(fit.motorMomentRms, 2)} / {num(fit.motor.momentContinuous, 2)} Nm
                  </Td>
                  <Td numeric className={aus > 1 ? 'cell-warn' : undefined}>
                    {pct(aus, 0)}
                  </Td>
                  <Td>
                    {fit.problems.length > 0 ? (
                      <span className="cell-warn">{fit.problems.join('; ')}</span>
                    ) : (
                      <Badge tone="success">hält</Badge>
                    )}
                    {fit.unknowns.length > 0 && (
                      <div className="cell-muted">{fit.unknowns.length} Grösse nicht bewertbar</div>
                    )}
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Card>

      {/*
        DER VORSCHLAG BLEIBT, ABER ER IST NICHT MEHR DIE ANTWORT.

        Er sucht im Katalog den kleinsten Motor, der passt. Das setzt voraus,
        dass hohe Übersetzungen sich selbst bestrafen — über die Rotorträgheit,
        die quadratisch mitwächst. Der Stand nennt sie für jeden Motor, bei
        FMK07019 und FMK06018 als Schätzung; der Halteverlust bleibt ohne
        Phasenwiderstand unbewertbar. Die Einzelprüfungen gelten, die REIHUNG
        hängt an den geschätzten Trägheiten — und das steht dran.
      */}
      <Card flush title="Katalogsuche">
        <Table>
          <thead>
            <tr>
              <Th>Achse</Th>
              <Th>Antrieb</Th>
              <Th numeric>i</Th>
              <Th numeric>n Rotor</Th>
              <Th numeric>M Spitze</Th>
              <Th numeric>M eff</Th>
              <Th numeric>Halteverlust</Th>
              <Th numeric>Km gefordert</Th>
              <Th numeric>J zulässig</Th>
              <Th>Befund</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((proposal) => (
              <ProposalRow
                key={`${proposal.demand.joint}-${proposal.demand.plane}`}
                proposal={proposal}
                row={axisRow(focus, setFocus, {
                  joint: proposal.demand.joint,
                  side: proposal.demand.side,
                  label: proposal.demand.label,
                })}
              />
            ))}
          </tbody>
        </Table>
      </Card>

      {/*
        DIE GETROFFENE ENTSCHEIDUNG, NEBEN DEM VORSCHLAG.

        Oben rechnet die Anwendung, was eine Achse braucht, und sucht im Katalog
        den kleinsten Motor, der es kann. Hier steht, was ein Mensch ausgewählt
        hat. Beides nebeneinander ist der Sinn der Seite: wo Vorschlag und
        Auswahl auseinandergehen, steht eine Frage — und ohne die zweite Tabelle
        kann man sie nicht stellen.
      */}
      <Card
        title="Gesetzte Motorisierung — Gen5 V0_24"
        eyebrow={`${SET_AXIS_COUNT} Achsen · ${SET_MOTOR_COUNT} Motoren · ${num(SET_MOTOR_MASS, 2)} kg Motormasse`}
        flush
      >
        <Table>
          <thead>
            <tr>
              <Th>Achse</Th>
              <Th numeric>Anzahl</Th>
              <Th>Motor</Th>
              <Th numeric>i</Th>
              <Th numeric>M Abgang</Th>
              <Th numeric>ω Abgang</Th>
              <Th numeric>Masse/Achse</Th>
              <Th>Stand</Th>
            </tr>
          </thead>
          <tbody>
            {SET_AXES.map((axis) => {
              const motor = MOTOR_BY_ID.get(axis.motorId)
              const ziel = targetFor(axis.key)
              return (
                <tr key={axis.key}>
                  <Td>
                    {axis.label}
                    {axis.note && <span className="cell-muted"> — {axis.note}</span>}
                  </Td>
                  <Td numeric>{axis.count}</Td>
                  <Td>
                    {axis.motorsPerAxis > 1 && (
                      <span className="cell-muted">{axis.motorsPerAxis}× </span>
                    )}
                    {motor?.label ?? axis.motorId}
                  </Td>
                  <Td numeric>
                    {num(axis.ratio, axis.ratio % 1 ? 2 : 0)}:1
                    {axis.rodRatio !== undefined && (
                      <span className="cell-muted"> · Stange {num(axis.rodRatio, 2)}</span>
                    )}
                  </Td>
                  <Td numeric>
                    {num(axis.momentOut, 0)} Nm
                    {ziel && (
                      <span className="cell-muted">
                        {' '}
                        / {num(ziel.moment[0], 0)}
                      </span>
                    )}
                  </Td>
                  <Td numeric>
                    {num(axis.speedOut, 1)} rad/s
                    {ziel && (
                      <span className="cell-muted"> / {num(ziel.speed[0], 0)}</span>
                    )}
                  </Td>
                  <Td numeric>{num(axis.mass, 3)} kg</Td>
                  <Td>
                    <Badge tone={STATUS_TONE[axis.status]}>{STATUS_LABEL[axis.status]}</Badge>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Card>

      <div className="chart-grid">
        <Card
          title="Motoren des Stands"
          eyebrow={`${CATALOG_MOTORS.length} Motoren · Kinco/Honpine FMK`}
          flush
        >
          <Table>
            <thead>
              <tr>
                <Th>Motor</Th>
                <Th numeric>Ø × H</Th>
                <Th numeric>M dauer</Th>
                <Th numeric>M spitze</Th>
                <Th numeric>n nenn / max</Th>
                <Th numeric>J Rotor</Th>
                <Th numeric>Masse</Th>
                <Th numeric>Aktor</Th>
              </tr>
            </thead>
            <tbody>
              {CATALOG_MOTORS.map((motor) => (
                <tr key={motor.id} title={motor.note}>
                  <Td>
                    {motor.label}
                    {motor.source === 'geschaetzt' && (
                      <>
                        {' '}
                        <Badge tone="neutral">teils geschätzt</Badge>
                      </>
                    )}
                  </Td>
                  <Td numeric>
                    {motor.diameter} × {num(motor.height, 1)} mm
                  </Td>
                  <Td numeric>{num(motor.momentNominal, 2)} Nm</Td>
                  <Td numeric>{num(motor.momentPeak, 2)} Nm</Td>
                  <Td numeric>
                    {motor.speedNominal} / {motor.speedMax} 1/min
                  </Td>
                  <Td numeric>{motor.rotorInertia.toExponential(2)}</Td>
                  <Td numeric>{num(motor.mass, 2)} kg</Td>
                  <Td numeric>{num(motor.actuatorMass, 2)} kg</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>

      {/*
        DIE OFFENEN PUNKTE STEHEN NEBEN DEN ZAHLEN, DIE SIE BLOCKIEREN.

        Einer davon hält diese Seite auf: ohne Phasenwiderstand gibt es keine
        Motorkonstante und damit keinen Halteverlust. In der Tabelle oben steht
        deshalb an dieser Stelle „nicht bewertbar" — und hier steht, was zu
        klären ist, damit das aufhört.

        Ein offener Punkt, der nur im Dokument steht, ist eine Notiz. Einer, der
        neben der blockierten Zahl steht, ist eine Aufgabe.
      */}
      <Card
        title="Offene Punkte und Annahmen des Stands"
        eyebrow={`${OPEN_QUESTIONS.filter((q) => q.blocks).length} von ${OPEN_QUESTIONS.length} blockieren die Auslegung`}
        flush
      >
        <Table>
          <thead>
            <tr>
              <Th>Punkt</Th>
              <Th>Was offen ist</Th>
              <Th>Wirkung hier</Th>
            </tr>
          </thead>
          <tbody>
            {OPEN_QUESTIONS.map((q) => (
              <tr key={q.key}>
                <Td>{q.topic}</Td>
                <Td>{q.needed}</Td>
                <Td>
                  {q.blocks ? (
                    <Badge tone="warning">blockiert</Badge>
                  ) : (
                    <span className="cell-muted">Konstruktion</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Alert tone="warning" title="Was die Auslegung nicht beurteilen kann">
        Der Katalog trägt die Werte des Gen5-Stands — Dauermoment, Spitzenmoment, Nenn- und
        Höchstdrehzahl, Rotorträgheit, Masse. Zwei Grössen fehlen und werden nicht geschätzt:
        <ul>
          {MISSING_MOTOR_DATA.map((zeile) => (
            <li key={zeile}>{zeile}</li>
          ))}
        </ul>
        Wo sie gebraucht werden, steht „nicht bewertbar" statt einer Zahl. Eine erfundene
        Konstante sähe richtig aus und wäre der schlechtere Zustand.
      </Alert>

      <Alert tone="warning" title="Nicht gerechnet">
        Das Getriebe ist eine Übersetzung und ein Wirkungsgrad, sonst nichts — seine
        Masse, sein zulässiges Abtriebsmoment, sein Verdrehspiel und seine Steifigkeit
        fehlen. Die Bauform fehlt ebenfalls: ob der Motor auf der Achse sitzt oder
        200 mm proximal, ändert die Trägheit der ganzen Kette. Und es zählt nur der
        gerechnete Zyklus; Treppe, Aufstehen, Stossbelastung und Sturz sind eigene
        Lastfälle.
      </Alert>
    </div>
  )
}

function ProposalRow({
  proposal,
  row,
}: {
  proposal: DriveProposal
  /* Dieselbe Markierung wie in der Anforderungstabelle: es ist dieselbe Achse,
     und wer sie dort angeklickt hat, soll sie hier wiederfinden. */
  row?: React.HTMLAttributes<HTMLTableRowElement>
}) {
  const best = proposal.candidates[0]
  if (!best) return null

  return (
    <tr {...row}>
      <Td>{proposal.demand.label}</Td>
      <Td>
        {best.motor.label}
        {!proposal.feasible && (
          <>
            {' '}
            <Badge tone="danger">passt nicht</Badge>
          </>
        )}
      </Td>
      <Td numeric>{num(best.ratio, 0)}</Td>
      <Td numeric className={best.use.speed > 1 ? 'cell-warn' : undefined}>
        {num(best.motorSpeed, 0)}
      </Td>
      <Td numeric className={best.use.peak > 1 ? 'cell-warn' : undefined}>
        {num(best.motorMomentPeak, 2)} Nm
      </Td>
      <Td numeric className={best.use.continuous > 1 ? 'cell-warn' : undefined}>
        {num(best.motorMomentRms, 2)} Nm
      </Td>
      {/* Ohne Phasenwiderstand gibt es keinen Halteverlust. Ein Strich ist hier
          die richtige Auskunft — eine 0 hiesse „kein Verlust". */}
      <Td numeric className={(best.use.holding ?? 0) > 1 ? 'cell-warn' : undefined}>
        {best.holdingLoss === null ? (
          <span className="cell-muted" title="Phasenwiderstand nicht veröffentlicht">
            nicht bewertbar
          </span>
        ) : (
          `${num(best.holdingLoss, 0)} W`
        )}
      </Td>
      <Td numeric>{num(proposal.required.motorConstant, 3)}</Td>
      <Td numeric>
        {proposal.required.rotorInertia === null ? (
          <span className="cell-muted">—</span>
        ) : (
          proposal.required.rotorInertia.toExponential(1)
        )}
      </Td>
      <Td>
        {best.problems.length === 0 ? (
          <span className="cell-muted">passt</span>
        ) : (
          <span className="cell-warn">{best.problems.join('; ')}</span>
        )}
      </Td>
    </tr>
  )
}

/* -------------------------------------------------------------------------
   Das Zehengrundgelenk
   ------------------------------------------------------------------------- */

/**
 * Die eine Achse, die keinen Antrieb bekommt.
 *
 * Sie steht auf DIESER Seite und nicht auf einer eigenen: die Frage „welchen
 * Motor braucht dieses Gelenk" wird hier gestellt, und die Antwort „keinen"
 * gehört an dieselbe Stelle wie die anderen. In der Tabelle oben taucht sie
 * nicht auf — wer sie dort suchte, fände nichts und schlösse daraus, das Gelenk
 * sei kräftefrei.
 */
function ToeCard({
  toe,
  limit,
}: {
  toe: ReturnType<typeof useSimulation>['simulation']['toe']
  limit: number
}) {
  /* Die Kennlinie als Kurve über dem Winkel — dieselbe Funktion, aus der auch
     die Auslenkung kommt. */
  const steps = 90
  const curve = Array.from({ length: steps + 1 }, (_, i) =>
    toeMoment((i / steps) * TOE_SPRING.stop),
  )

  return (
    <Card
      title="Zehengrundgelenk — Feder statt Antrieb"
      eyebrow={toe.on ? 'passiv · progressive Feder mit Vorspannung und Anschlag' : 'Feder abgeschaltet'}
    >
      <div className="metrics">
        <Metric
          label="Momentbedarf"
          value={`${num(toe.demand, 0)} Nm`}
          note={`zulässig ${num(limit, 0)} Nm`}
          warn={toe.demand > limit}
        />
        <Metric
          label="Federauslenkung"
          value={toe.on ? `${num(toe.deflection, 1)} °` : '—'}
          note={toe.on ? `Rest bis Anschlag ${num(toe.headroom, 1)} °` : 'starre Zehe'}
          warn={toe.bottomed}
        />
        <Metric
          label="Gespeichert"
          value={toe.on ? `${num(toe.stored, 1)} J` : '—'}
          note={toe.on ? `davon ${num(toe.returned, 1)} J zurück` : 'die Energie verschwindet'}
        />
        <Metric
          label="Zehe unter Last"
          value={`${num(100 * toe.loaded, 0)} %`}
          note="des Zyklus, beide Seiten"
        />
      </div>

      <div className="toe-split">
        <div>
          <span className="eyebrow">Kennlinie</span>
          <CycleChart
            title="Federmoment"
            unit="Nm"
            height={190}
            zero={false}
            foot={false}
            xDomain={[0, TOE_SPRING.stop]}
            xUnit="°"
            xName="dem Auslenkwinkel"
            xLabel="Auslenkung"
            yLabel="Moment"
            series={[{ key: 'spring', label: 'Feder', values: curve, tone: 1 }]}
          />
          <p className="metric__note">
            Vorspannung {TOE_SPRING.preload} Nm, Rate {String(TOE_SPRING.rate).replace('.', ',')}{' '}
            Nm/Grad, progressiv ab {num(0.7 * TOE_SPRING.stop, 1)} °, Anschlag {TOE_SPRING.stop} °,
            Rückgabe {Math.round(100 * TOE_SPRING.recovery)} %.
          </p>
        </div>

        <div className="stack stack--tight">
          <div>
            <span className="eyebrow">Warum kein Motor</span>
            <p className="page-lead">
              Das Gelenk nimmt über den Zyklus nur Energie auf — in jeder Gangart ist die
              Nettoarbeit negativ. Dazu die Trägheitsregel, die schon P00 am Knie begründet: 0,8 kg
              Antrieb am Vorfuss sitzen rund einen Meter von der Hüfte und bringen dort etwa
              0,8 kg·m² ein, mehr als der ganze Gewinn der proximalen Kniekonstruktion. Man
              bezahlte zweimal.
            </p>
          </div>
          <div>
            <span className="eyebrow">Wann die Zehe trägt</span>
            <p className="page-lead">
              Erst, wenn der Druckmittelpunkt vor ihrer Achse liegt. Solange er dahinter sitzt,
              bleibt das Gelenk kräftefrei. Das Moment ist die senkrechte Bodenkraft mal dem Hebel
              nach vorn, abzüglich der waagerechten Kraft mal dem Hebel nach oben.
            </p>
          </div>
          <div>
            <span className="eyebrow">Grenze des Modells</span>
            <p className="page-lead">
              Der Zehenwinkel im Gangbild ist vorgegeben und federt nicht mit. Die Auslenkung wird
              deshalb über die Kennlinie zum geforderten Moment gerechnet und nicht aus dem
              Winkelkanal gelesen. Ein mitfederndes Gelenk verschöbe die Druckmittelpunktbahn
              leicht; für die Auslegung der Feder genügt dieser Weg.
            </p>
          </div>
          <div>
            <span className="eyebrow">Was die Feder nicht kann</span>
            <p className="page-lead">
              Keine gangabhängige Steifigkeit — Gehen möchte weicher, der Sprint steifer. Und
              keinen aktiven Zehenabdruck am Ende der Standphase. Dafür gibt es etwas geschenkt:
              ein Winkelgeber an der Zehe liefert über die bekannte Kennlinie unmittelbar die
              Zehenlast, ein Kontaktsignal ohne eigenen Kraftsensor.
            </p>
          </div>
        </div>
      </div>
    </Card>
  )
}
